import React from "react";
import { motion } from "framer-motion";
import {
  CalendarCheck,
  CalendarDays,
  Clock3,
  Stethoscope,
  Video,
  Building2,
  UserRound,
  Check,
  ArrowRight,
  ShieldCheck,
  Bell,
  RefreshCw,
  FileText,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";
import { Link } from "react-router-dom";

const Appointments = () => {
  const appointmentTypes = [
    {
      icon: Stethoscope,
      title: "Doctor Appointment",
      description:
        "Schedule an appointment with a healthcare professional based on your care needs.",
    },
    {
      icon: Video,
      title: "Virtual Consultation",
      description:
        "Choose a supported virtual consultation and connect with your healthcare professional online.",
    },
    {
      icon: Building2,
      title: "In-Clinic Appointment",
      description:
        "Book a visit with a participating healthcare provider or clinic.",
    },
    {
      icon: UserRound,
      title: "Follow-Up Care",
      description:
        "Continue your care with a follow-up appointment when recommended by your provider.",
    },
  ];

  const steps = [
    {
      number: "01",
      icon: UserRound,
      title: "Choose a Provider",
      description:
        "Find a healthcare professional or clinic that matches your care needs.",
    },
    {
      number: "02",
      icon: CalendarDays,
      title: "Select a Date",
      description:
        "View available appointment dates and choose a convenient option.",
    },
    {
      number: "03",
      icon: Clock3,
      title: "Choose a Time",
      description:
        "Select an available time slot that works for you.",
    },
    {
      number: "04",
      icon: CalendarCheck,
      title: "Confirm",
      description:
        "Review your appointment details and confirm your booking.",
    },
  ];

  const benefits = [
    "View available appointment options",
    "Choose virtual or in-clinic care where supported",
    "Select a convenient date and time",
    "Keep appointment information organised",
    "Manage upcoming appointments",
    "Continue care through connected Doxzon workflows",
  ];

  const faqs = [
    {
      question: "How do I book an appointment?",
      answer:
        "Find a supported healthcare professional or clinic, select an available appointment option, choose a date and time, and confirm the booking.",
    },
    {
      question: "Can I book a virtual consultation?",
      answer:
        "Where virtual consultation is supported by the healthcare professional, you can select the available virtual appointment option.",
    },
    {
      question: "Can I choose my appointment time?",
      answer:
        "Available dates and time slots depend on the healthcare professional's schedule.",
    },
    {
      question: "Can I manage my upcoming appointments?",
      answer:
        "Supported appointment management allows patients to keep track of their upcoming care and relevant appointment details.",
    },
    {
      question: "Can I book a follow-up appointment?",
      answer:
        "Where follow-up scheduling is supported, patients can continue their care through a subsequent appointment.",
    },
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-teal-200/25 blur-3xl" />

            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-200/25 blur-3xl" />

            <div
              className="absolute inset-0 opacity-[0.025]"
              style={{
                backgroundImage:
                  "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Appointments
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Book care
                <span className="block text-teal-500 mt-2">
                  around your time.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Find a suitable healthcare professional, choose an available
                date and time, and manage your appointment through one
                connected experience.
              </p>

              {/* SEARCH / BOOKING BAR */}
              <div className="mt-9 rounded-2xl bg-white border border-slate-100 p-2 shadow-[0_20px_60px_rgba(15,23,42,0.10)]">

                <div className="grid md:grid-cols-3 gap-2">

                  <div className="flex items-center gap-3 px-4 h-14 rounded-xl bg-slate-50">
                    <Stethoscope
                      size={18}
                      className="text-teal-600 shrink-0"
                    />

                    <input
                      type="text"
                      placeholder="Doctor or specialty"
                      className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
                    />
                  </div>

                  <div className="flex items-center gap-3 px-4 h-14 rounded-xl bg-slate-50">
                    <CalendarDays
                      size={18}
                      className="text-teal-600 shrink-0"
                    />

                    <input
                      type="text"
                      placeholder="Choose a date"
                      className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="h-14 rounded-xl bg-slate-950 text-white inline-flex items-center justify-center gap-2 font-semibold text-sm"
                  >
                    Find Appointment
                    <ArrowRight size={16} />
                  </motion.button>

                </div>

              </div>

              <div className="flex flex-wrap gap-6 mt-7 text-sm text-slate-500">

                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={16} className="text-teal-500" />
                  Connected healthcare
                </span>

                <span className="inline-flex items-center gap-2">
                  <CalendarCheck size={16} className="text-teal-500" />
                  Flexible scheduling
                </span>

              </div>

            </motion.div>

            {/* RIGHT APPOINTMENT CARD */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative bg-white rounded-[30px] border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-6 md:p-7">

                <div className="flex items-center justify-between pb-5 border-b border-slate-100">

                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Appointment
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Book Your Visit
                    </h3>
                  </div>

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">
                    <CalendarCheck
                      size={21}
                      className="text-teal-600"
                    />
                  </div>

                </div>

                {/* PROVIDER */}
                <div className="flex items-center gap-4 mt-6 p-4 rounded-2xl bg-slate-50">

                  <div className="w-12 h-12 rounded-xl bg-teal-100 grid place-items-center">
                    <Stethoscope
                      size={22}
                      className="text-teal-700"
                    />
                  </div>

                  <div className="flex-1">

                    <p className="font-semibold text-sm">
                      Healthcare Professional
                    </p>

                    <p className="text-xs text-slate-400 mt-1">
                      Specialist consultation
                    </p>

                  </div>

                  <span className="text-xs font-semibold text-teal-600">
                    Available
                  </span>

                </div>

                {/* DATE */}
                <div className="mt-5">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-semibold">
                      Select date
                    </p>

                    <CalendarDays
                      size={17}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="grid grid-cols-4 gap-2">

                    {[
                      ["MON", "18"],
                      ["TUE", "19"],
                      ["WED", "20"],
                      ["THU", "21"],
                    ].map(([day, date], index) => (

                      <div
                        key={date}
                        className={`rounded-xl p-3 text-center border ${
                          index === 1
                            ? "bg-teal-500 text-white border-teal-500"
                            : "bg-white border-slate-100"
                        }`}
                      >

                        <p className="text-[9px] font-bold tracking-wider opacity-70">
                          {day}
                        </p>

                        <p className="text-lg font-bold mt-1">
                          {date}
                        </p>

                      </div>

                    ))}

                  </div>

                </div>

                {/* TIME */}
                <div className="mt-5">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-semibold">
                      Available time
                    </p>

                    <Clock3
                      size={17}
                      className="text-teal-600"
                    />

                  </div>

                  <div className="grid grid-cols-3 gap-2">

                    {["09:30 AM", "11:00 AM", "02:30 PM"].map(
                      (time, index) => (

                        <div
                          key={time}
                          className={`rounded-xl py-2.5 text-center text-xs font-semibold border ${
                            index === 1
                              ? "bg-slate-950 text-white border-slate-950"
                              : "bg-white border-slate-100 text-slate-600"
                          }`}
                        >
                          {time}
                        </div>

                      )
                    )}

                  </div>

                </div>

                <button className="w-full mt-6 h-12 rounded-xl bg-teal-500 text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
                  Confirm Appointment
                  <Check size={16} />
                </button>

              </div>

            </motion.div>

          </div>
        </section>

        {/* =====================================================
            APPOINTMENT TYPES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Appointment Options
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Care that fits
                <span className="text-teal-500">
                  {" "}your schedule.
                </span>
              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Choose from supported appointment and consultation options
                based on your healthcare needs.
              </p>

            </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">

  {appointmentTypes.map((item, index) => {
    const Icon = item.icon;

    return (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{
          duration: 0.5,
          delay: index * 0.06,
        }}
        whileHover={{ y: -4 }}
        className="group rounded-xl border border-slate-100 bg-slate-50/70 p-4 hover:bg-white hover:shadow-md hover:border-teal-100 transition"
      >

        <div className="w-9 h-9 rounded-lg bg-teal-50 grid place-items-center group-hover:bg-teal-500 transition">
          <Icon
            size={17}
            className="text-teal-600 group-hover:text-white transition"
          />
        </div>

        <h3 className="font-sora text-sm font-semibold mt-3">
          {item.title}
        </h3>

        <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
          {item.description}
        </p>

      </motion.div>
    );
  })}

</div>
          </div>

        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Booking made
                <span className="text-teal-500">
                  {" "}simple.
                </span>
              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-teal-100" />

              {steps.map((item) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="relative text-center"
                  >

                    <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-950 border-4 border-[#f6fbfc] shadow-lg grid place-items-center">

                      <Icon
                        size={21}
                        className="text-teal-400"
                      />

                    </div>

                    <span className="block mt-5 text-[10px] font-bold tracking-[0.2em] text-teal-600">
                      STEP {item.number}
                    </span>

                    <h3 className="font-sora text-lg font-semibold mt-2">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            APPOINTMENT MANAGEMENT
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Manage Your Care
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Your appointments,
                <span className="block text-teal-500">
                  organised in one place.
                </span>
              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Keep track of upcoming appointments and relevant appointment
                information through your Doxzon healthcare journey.
              </p>

            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-4">

              {[
                [CalendarCheck, "Upcoming Appointments"],
                [Bell, "Appointment Reminders"],
                [RefreshCw, "Rescheduling"],
                [FileText, "Appointment Details"],
              ].map(([Icon, title], index) => (

                <motion.div
                  key={title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.07 }}
                  whileHover={{ y: -5 }}
                  className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm"
                >

                  <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                    <Icon
                      size={20}
                      className="text-teal-600"
                    />

                  </div>

                  <h3 className="font-semibold mt-4">
                    {title}
                  </h3>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="py-24 md:py-28 bg-gradient-to-br from-cyan-50 to-blue-50">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Patient Benefits
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Spend less time
                <span className="block text-teal-500">
                  managing appointments.
                </span>
              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Doxzon helps make appointment discovery, scheduling and
                ongoing care easier to manage.
              </p>

            </div>

            <div className="space-y-3">

              {benefits.map((item, index) => (

                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-white bg-white/80 p-4 shadow-sm"
                >

                  <span className="w-8 h-8 rounded-full bg-teal-50 text-teal-600 grid place-items-center shrink-0">
                    <Check size={15} />
                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Frequently asked questions
              </h2>

            </div>

            <div className="space-y-3 mt-12">

              {faqs.map((faq, index) => (

                <motion.details
                  key={faq.question}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group rounded-2xl border border-slate-100 bg-slate-50 open:bg-white open:shadow-sm"
                >

                  <summary className="cursor-pointer list-none flex items-center justify-between gap-5 p-6 font-sora font-semibold">

                    <span>
                      {faq.question}
                    </span>

                    <span className="w-8 h-8 rounded-full bg-white group-open:bg-teal-500 group-open:text-white grid place-items-center shrink-0 transition">

                      <ArrowRight
                        size={15}
                        className="group-open:rotate-90 transition-transform"
                      />

                    </span>

                  </summary>

                  <p className="px-6 pb-6 text-slate-600 leading-7 text-sm md:text-base">
                    {faq.answer}
                  </p>

                </motion.details>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-slate-950 text-white px-8 py-16 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-500/15 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />

            <div className="relative">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Schedule Your Care
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Book your next
                <span className="text-teal-400">
                  {" "}appointment.
                </span>
              </h2>

              <p className="text-slate-300 max-w-2xl mx-auto mt-5 leading-7">
                Choose a healthcare professional, select a convenient time
                and continue your care journey with Doxzon.
              </p>

           <Link
  to="/consultdoctor"
  className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-7 py-4 font-semibold hover:bg-teal-50 transition"
>
  Book an Appointment
  <ArrowRight size={17} />
</Link>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Appointments;